// Messages : une conversation par demande déposée (messagerie du socle, H8-01), avec le nombre de
// messages non lus et le dernier message lus dans la fiche de chaque demande (sans marquer lu),
// puis l'écran de conversation d'une demande (lecture, envoi, pièces de la demande).
import { EtatVide } from '@cc/ui';
import { MessageSquare } from 'lucide-react';
import { useParams } from 'react-router';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date, pluriel } from '../../../commun/format';
import { useConversationsDesDossiers, useDossierUsager, type LigneConversation } from '../api';
import { EnTeteCitoyen, ExigeConnexion, Ligne, Liste, TexteStatut } from '../commun';
import { dernierMessage, libelleNonLus, resumeConversation } from '../conversation';
import { Conversation } from './Conversation';

/** Dernière activité d'une conversation : dernier message, sinon dépôt de la demande. */
function activite(l: LigneConversation): number {
  const dernier = l.conversation ? dernierMessage(l.conversation) : null;
  return new Date(dernier?.horodatage ?? l.dossier.enregistre_le ?? l.dossier.cree_le).getTime();
}

function sousTitre(l: LigneConversation): string {
  const base = [l.dossier.numero ?? '', l.dossier.collectivite.nom].filter(Boolean).join(' · ');
  if (l.conversation) return `${base} · ${resumeConversation(l.conversation)}`;
  return l.erreur ? `${base} · conversation indisponible pour l'instant` : base;
}

export function PageMessages() {
  const { dossiers, lignes, nonLus } = useConversationsDesDossiers();
  const triees = [...lignes].sort((a, b) => (b.conversation?.non_lus ?? 0) - (a.conversation?.non_lus ?? 0) || activite(b) - activite(a));
  return (
    <>
      <EnTeteCitoyen
        titre="Messages"
        sousTitre={nonLus ? `${pluriel(nonLus, 'message non lu', 'messages non lus')} dans vos conversations.` : 'Une conversation par demande déposée, avec le service qui la traite.'}
      />
      <ExigeConnexion titre="Connectez-vous pour lire vos messages">
        <EtatRequete chargement={dossiers.isPending} erreur={dossiers.error} onReessayer={() => void dossiers.refetch()} fonction="Vos conversations" lignes={4}>
          {triees.length === 0 ? (
            <EtatVide titre="Aucune conversation" icone={MessageSquare}>
              <p>Chaque demande déposée ouvre une conversation avec le service qui la traite. Vous n&apos;avez pas encore de demande déposée.</p>
            </EtatVide>
          ) : (
            <Liste libelle="Conversations">
              {triees.map((l) => (
                <Ligne
                  key={l.dossier.id}
                  href={`/citoyen/mes-dossiers/${l.dossier.id}/messages`}
                  titre={l.dossier.demarche.libelle}
                  sousTitre={sousTitre(l)}
                  droite={
                    l.conversation && l.conversation.non_lus > 0 ? <TexteStatut libelle={libelleNonLus(l.conversation.non_lus)} ton="attention" /> : <TexteStatut code={l.dossier.statut.code} />
                  }
                />
              ))}
            </Liste>
          )}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}

export function PageConversation() {
  const { id = '' } = useParams();
  const dossier = useDossierUsager(id || null);
  const d = dossier.data;
  return (
    <>
      <EnTeteCitoyen
        titre="Messages avec le service"
        surTitre={d ? `${d.demarche.libelle} · ${d.numero ?? ''}` : undefined}
        sousTitre={d?.enregistre_le ? `Demande déposée le ${date(d.enregistre_le)} auprès de ${d.collectivite.nom}` : undefined}
        retour={`/citoyen/mes-dossiers/${id}`}
        libelleRetour="Suivi de la demande"
      />
      <ExigeConnexion>
        <EtatRequete chargement={dossier.isPending} erreur={dossier.error} onReessayer={() => void dossier.refetch()} fonction="Cette demande" lignes={5}>
          {d ? <Conversation d={d} /> : null}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
